/**
 * Publica no Instagram via Meta Graph API.
 * IMPORTANTE: a Meta exige que as imagens estejam em **URL pública** (HTTPS).
 * Não aceita dataURL direto. Estratégias:
 *  1. Já tem URL pública → posta direto
 *  2. Cliente do app envia dataURLs pra um endpoint próprio (/uploads na API) que devolve URL pública
 *  3. Usa MinIO/S3 da Contabo
 *
 * Esta lib cuida do fluxo de publicação assumindo URLs públicas já prontas.
 */

const META_API = "https://graph.facebook.com/v21.0";

export interface IGAccount {
  accountId: string;
  accessToken: string;
}

export interface IGCarouselItem {
  imageUrl: string;        // URL pública HTTPS
}

/** Cria um container de mídia (single image OU carousel item). */
async function createMediaContainer(account: IGAccount, params: Record<string, string>): Promise<string> {
  const url = new URL(`${META_API}/${account.accountId}/media`);
  Object.entries(params).forEach(([k, v]) => url.searchParams.append(k, v));
  url.searchParams.append("access_token", account.accessToken);
  const res = await fetch(url.toString(), { method: "POST" });
  const json = await res.json();
  if (json.error) throw new Error(`Meta API: ${json.error.message}`);
  return json.id;
}

/** Publica um container já pronto. */
async function publishContainer(account: IGAccount, containerId: string): Promise<string> {
  const url = new URL(`${META_API}/${account.accountId}/media_publish`);
  url.searchParams.append("creation_id", containerId);
  url.searchParams.append("access_token", account.accessToken);
  const res = await fetch(url.toString(), { method: "POST" });
  const json = await res.json();
  if (json.error) throw new Error(`Meta publish: ${json.error.message}`);
  return json.id;
}

export async function publishSingleImage(account: IGAccount, imageUrl: string, caption: string): Promise<string> {
  const containerId = await createMediaContainer(account, {
    image_url: imageUrl,
    caption: caption.slice(0, 2200)
  });
  // dá um tempinho pra Meta processar
  await new Promise(r => setTimeout(r, 3000));
  return publishContainer(account, containerId);
}

export async function publishCarousel(account: IGAccount, imageUrls: string[], caption: string): Promise<string> {
  if (imageUrls.length < 2 || imageUrls.length > 10) {
    throw new Error("Carrossel Instagram: 2-10 imagens.");
  }
  // 1. Cria container de cada item
  const childIds: string[] = [];
  for (const imageUrl of imageUrls) {
    const id = await createMediaContainer(account, {
      image_url: imageUrl,
      is_carousel_item: "true"
    });
    childIds.push(id);
  }
  // 2. Cria container do carousel
  const carouselId = await createMediaContainer(account, {
    media_type: "CAROUSEL",
    children: childIds.join(","),
    caption: caption.slice(0, 2200)
  });
  // 3. Aguarda + publica
  await new Promise(r => setTimeout(r, 4000));
  return publishContainer(account, carouselId);
}

/** Faz upload de dataURL pra endpoint próprio que devolve URL pública. */
export async function uploadDataUrl(apiBase: string, authToken: string, dataUrl: string, filename: string): Promise<string> {
  // converte dataURL pra Blob
  const res = await fetch(dataUrl);
  const blob = await res.blob();
  const fd = new FormData();
  fd.append("file", blob, filename);
  const upRes = await fetch(`${apiBase}/uploads`, {
    method: "POST",
    headers: { "authorization": `Bearer ${authToken}` },
    body: fd
  });
  if (!upRes.ok) throw new Error(`Upload ${upRes.status}: ${(await upRes.text()).slice(0, 200)}`);
  const json = await upRes.json();
  if (!json.url) throw new Error("Endpoint /uploads não retornou URL.");
  return json.url;
}
